import { gql } from '@apollo/client';
import { BASIC_LOCATION_FIELDS_FRAGMENT } from './fragments/location';
import {
    SIMPLE_PROJECT_FIELDS_FRAGMENT,
    PLANNING_PROJECT_LESS_FIELDS_FRAGMENT
} from './fragments/project';
import { BASIC_TASK_FIELDS_FRAGMENT } from './fragments/task';
import {
    FR_USER_AUTOCOMPLETE_FIELDS,
    USER_FIELDS_FRAGMENT,
    USER_IMAGE_FRAGMENT,
    USER_WITH_IMAGE_FRAGMENT
} from './fragments/user';
import {
    PLANNING_WORKPACKAGE_FIELDS_FRAGMENT,
    WORKPACKAGE_FIELDS_FRAGMENT,
    WORKPACKAGE_SUMMARY_FIELDS_FRAGMENT
} from './fragments/workpackage';

export function getUserFields() {
    return `
	  Firstname
	  Lastname
	  UserId
	  Phone
	  Email
	  Abbr
	  Access {
		Read
		Update
		Delete
		Create
	  }
	  Title
	  UserState
	  ProjectCount
	  Created
	  OwnOrganization {
		Name
		OrganizationId
	  }
	  LastUpdate
	  OrganizationAdmin
	  SystemAdmin
		Subscription {
			SubscriptionId
			UserGroup {
				Name
				UserGroupId
				Organization {
					OrganizationId
					Name
				}
			}
			Plan {
				Name
				PlanId
			}
		}`;
}

export const user_fields = `
	Firstname
	Lastname
	Fullname
	UserId
	Phone
	Email
	Abbr
	Access {
		Read
		Update
		Delete
		Create
	}
	Title
	UserState
	ProjectCount
	Created
	OwnOrganization {
		Name
		OrganizationId
	}
	LastUpdate
	OrganizationAdmin
	SystemAdmin
	isGMWorker
	isResourceManager
	LastLogin
`;

export const logged_in_user_fields = `
		Firstname
		Lastname
		UserId
		Phone
		Email
		Abbr
		Access {
			Read
			Update
			Delete
			Create
		}
		Title
		UserState
		ProjectCount
		Created
		OwnOrganization {
			OrganizationId
			TaskGroup
			Name
			Theme{
				ThemeId
				PrimaryColor
				PrimaryTextColor
				SecondaryColor
				SecondaryTextColor
				DashboardBgColor
				WarningColor
				SuccessColor
				DashboardImage
				DashboardBgImage
				OrganizationLogo
				LoginBackgroundImage
			}
		}
		LastUpdate
		OrganizationAdmin
		SystemAdmin`;

export const user_basic =
    user_fields +
    `
		UserImage {
			FileId
		}`;

export const user_with_image =
    user_fields +
    `
		UserImage {
			FileId
			FileName
			FileType {
			FileTypeId
			Name
			}
			ContainerName
			MimeType
			FileData
		}`;

const user_options = `
		UserOptions {
			OptionId
			Name
			Group
			Header {
			  Text
			  LangCode
			  WidgetTextType {
				WidgetTextTypeId
				Name
			  }
			}  
		  }
`;
const user_groups_in = `
UserInGroup {
	UserInGroupId
	UserGroup {
		UserGroupId
		Name
		Plans {
			PlanId
			Name
		}
		Organization {
			Name
			OrganizationId
		}
	  UserGroupType {
			Name
			MembersMin
			MembersMax
	  }
	  Subscription {
		Plan {
		  PlanId
		  UserGroupType {
			Name
			MembersMin
			MembersMax
		  }
		  Name
		  PlanPrice {
			PlanPriceId
			Value
			Currency
		  }
		  Software {
			Name
		  }
		  Organization {
			Name
			OrganizationId
		  }
		}
	  }
	}
  }`;

const user_organization_role = `
  UserOrganizationRole {
	Organization {
	  OrganizationId
	  Name
	  Created
	  Updated
	}
	UserOrganizationRoleType {
	  UserOrganizationRoleTypeId
	  Name
	  Created
	  Updated
	}
	UserOrganizationRoleId
	UserOrganizationRoleTypeId
	UserId
	OrganizationId
  }`;

const user_image = `
	UserImage {
		FileId
		FileData
		MimeType
	}
 `;

export const GET_USER = gql(
    `query User { 
		User { ` +
        logged_in_user_fields +
        user_with_image +
        user_options +
        user_groups_in +
        user_organization_role +
        `}
	}`
);

export const GET_USER_BY_ID = gql(
    `query userById($UserId: Int!) { userById(data: {UserId: $UserId}) {` +
        user_with_image +
        user_options +
        user_groups_in +
        user_organization_role +
        `}}`
);

export const GET_USERIMAGE = gql(
    `query userImage($UserId: Int!) { userImage(data: {UserId: $UserId}) { UserId, Firstname, Lastname, Abbr,` +
        user_image +
        `}}`
);

export const GET_USERS_TO_SHARE_MODAL = gql`
    query getUsersToShareModal {
        Users {
            ...UserFields
            UserImage {
                ...UserImageFields
            }
        }
    }
    ${USER_FIELDS_FRAGMENT}
    ${USER_IMAGE_FRAGMENT}
`;

export const GET_USERS = gql(
    `query User { Users {` +
        user_basic +
        user_options +
        user_groups_in +
        user_organization_role +
        `}}`
);

export const GET_PLANNNING_USER_PROJECTS = gql`
    query PlanningUserProjects {
        Users {
            ...UserFieldsWithImage
            Projects(data: { UserProjectRoleType: [PROJECT_OWNER] }) {
                ...PlanningProjectLessFields
            }
        }
    }
    ${USER_WITH_IMAGE_FRAGMENT}
    ${PLANNING_PROJECT_LESS_FIELDS_FRAGMENT}
`;

export const GET_PLANNNING_WORKPACKAGE_USERS = gql`
    query PlanningWorkPackageUsers {
        Users {
            ...UserFieldsWithImage
            WorkPackages {
                ...PlanningWorkPackageFields
                WorkPackageSummary {
                    ...WorkPackageSummaryFields
                }
            }
        }
    }
    ${USER_WITH_IMAGE_FRAGMENT}
    ${PLANNING_WORKPACKAGE_FIELDS_FRAGMENT}
    ${WORKPACKAGE_SUMMARY_FIELDS_FRAGMENT}
`;

export const GET_USERS_EPICS = gql`
    query PlanningUsersEpics {
        Users {
            ...UserFieldsWithImage
            Epics {
                EpicId
                EpicNbr
                Name
                WorkPackage {
                    WorkPackageId
                    WorkNbr
                    WorkName
                    WorkPackageSummary {
                        ...WorkPackageSummaryFields
                    }
                }
            }
        }
    }
    ${WORKPACKAGE_SUMMARY_FIELDS_FRAGMENT}
    ${USER_WITH_IMAGE_FRAGMENT}
`;

export const GET_USERS_TASKS = gql`
    query User {
        Users {
            ...UserFieldsWithImage
            WorkPackages {
                ...WorkPackageFields
                WorkPackageSummary {
                    ...WorkPackageSummaryFields
                }
            }
            Projects {
                ...SimpleProjectFields
                Tasks {
                    ...BasicTaskFields
                    Location {
                        ...BasicLocationFields
                    }
                }
            }
        }
    }
    ${WORKPACKAGE_FIELDS_FRAGMENT}
    ${WORKPACKAGE_SUMMARY_FIELDS_FRAGMENT}
    ${USER_WITH_IMAGE_FRAGMENT}
    ${SIMPLE_PROJECT_FIELDS_FRAGMENT}
    ${BASIC_TASK_FIELDS_FRAGMENT}
    ${BASIC_LOCATION_FIELDS_FRAGMENT}
`;

export const UPDATE_USERSTATE = gql(
    `
mutation changeUserState($userid: Int!, $userState: UserState!) {
	changeUserState(data:{userId: $userid, userState: $userState}) {
		` +
        user_basic +
        user_options +
        user_groups_in +
        user_organization_role +
        `
	}
}
`
);

export const GET_PASSWORD = gql`
    mutation getPassword($email: String!) {
        getPassword(data: { identifier: $email }) {
            userid
            password
        }
    }
`;

export const DO_LOGIN = gql`
    mutation Login($code: String!, $userid: Int!) {
        Login(data: { password: $code, userid: $userid }) {
            UserId
        }
    }
`;

export const DO_LOGOUT = gql`
    mutation Logout {
        Logout {
            Success
        }
    }
`;

export const CREATE_USER = gql`
    mutation createUser(
        $firstName: String!
        $lastName: String!
        $email: String!
        $phone: String!
        $hash: String!
    ) {
        createUser(
            data: {
                firstName: $firstName
                lastName: $lastName
                email: $email
                phonenumber: $phone
                hash: $hash
            }
        ) {
            UserId
        }
    }
`;

export const CREATE_CONTACT = gql`
mutation createContact(
	$OrganizationId: Int!
	$Firstname: String!
	$Lastname: String!
	$Email: String!
	$Phone: String!
	$Title: String
	$Abbr: String
	$AddUserGroups: CreateContactAddUserGroup
	$AddOrganizationRoles: [CreateContactAddUserRole!]
) {
	createContact(
		data:{
			OrganizationId: $OrganizationId
			Firstname: $Firstname
			Lastname: $Lastname
			Email: $Email
			Phone: $Phone
			Title: $Title
			Abbr: $Abbr
			AddUserGroups: $AddUserGroups
			AddOrganizationRoles: $AddOrganizationRoles
		}
	) {
		${user_basic}
		${user_options}
		${user_groups_in}
		${user_organization_role}
	}
}`;

export const UPDATE_USER = gql`
mutation updateUser(
	$UserId: Int!
	$Firstname: String
	$Lastname: String
	$Email: String
	$Phone: String
	$Abbr: String
	$Title: String
	$OrganizationId: Int
	$AddUserGroups: UpdateUserAddUserGroupInputType
  $RemoveUserGroups: UpdateUserRemoveUserGroupInputType

	$RemoveUserOrganizationRoles: [UpdateUserRemoveOrganizationRoleInputType!]
	$AddUserOrganizationRoles: [UpdateUserAddOrganizationRoleInputType!]
) {
	updateUser(
		data:{
			UserId: $UserId
			Firstname: $Firstname
			Lastname: $Lastname
			Email: $Email
			Phone: $Phone
			Abbr: $Abbr
			Title: $Title
			OrganizationId: $OrganizationId
			RemoveUserOrganizationRoles: $RemoveUserOrganizationRoles
			AddUserOrganizationRoles: $AddUserOrganizationRoles
			AddUserGroups: $AddUserGroups
  		RemoveUserGroups: $RemoveUserGroups
		}
	) {
		${user_with_image}
		${user_options}
		${user_groups_in}
		${user_organization_role}
		${user_basic}
	}
}`;

export const GET_USERORGANIZATIONS_ROLETYPES = gql`
    query userOrganizationRoleTypes {
        userOrganizationRoleTypes {
            Created
            Name
            Updated
            UserOrganizationRoleTypeId
        }
    }
`;

export const ADD_USERORGANIZATIONS_ROLE = gql(
    `
mutation addUserOrganizationRole($UserId: Int!, $UserOrganizationRoleTypeId: Int!, $OrganizationId: Int!) {
	addUserOrganizationRole(data: {
		UserId: $UserId
		UserOrganizationRoleTypeId: $UserOrganizationRoleTypeId
		OrganizationId: $OrganizationId
	}) {` +
        user_with_image +
        user_options +
        user_groups_in +
        user_organization_role +
        `}}`
);

export const REMOVE_USERORGANIZATIONS_ROLE = gql(
    `
mutation removeUserOrganizationRole($UserId: Int!, $UserOrganizationRoleTypeId: Int!, $OrganizationId: Int!) {
	removeUserOrganizationRole(data: {
		UserId: $UserId
		UserOrganizationRoleTypeId: $UserOrganizationRoleTypeId
		OrganizationId: $OrganizationId
	}) {` +
        user_with_image +
        user_options +
        user_groups_in +
        user_organization_role +
        `}}`
);

export const UPLOAD_PROFILE_PIC = gql(
    `
	mutation uploadProfilePicture($File: Upload!, $UserId: Int!) {
		uploadProfilePicture(data: {File: $File, UserId: $UserId}) 
		  {` +
        user_with_image +
        user_options +
        user_groups_in +
        user_organization_role +
        `}}`
);

export const GET_USER_ROWS = gql`
    query getUserRows($From: Int, $To: Int, $Search: String) {
        userRows(data: { From: $From, To: $To, Search: $Search }) {
            TotalRowCount
            Users {
                ...fr_UserAutocompleteFields
            }
        }
    }
    ${FR_USER_AUTOCOMPLETE_FIELDS}
`;
